import { useState, useEffect } from "react";
import { DemoHint } from "@/components/demo/DemoHint";
import { RotateCcw, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { PostLoadCard } from "@/components/post-load/PostLoadCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface LoadTemplate {
  id: string;
  name: string;
  origin_city: string;
  origin_state: string;
  origin_latitude: number | null;
  origin_longitude: number | null;
  origin_address: string | null;
  origin_name: string | null;
  origin_country: string | null;
  origin_postal_code: string | null;
  destination_city: string;
  destination_state: string;
  destination_latitude: number | null;
  destination_longitude: number | null;
  destination_address: string | null;
  destination_name: string | null;
  destination_country: string | null;
  destination_postal_code: string | null;
  equipment_type: string;
  weight_lbs: number | null;
  length_ft: number | null;
  commodity: string | null;
  load_type: string | null;
  comments: string | null;
  pick_hrs: string | null;
  drop_hrs: string | null;
  usage_count: number;
}

interface Props {
  onUseTemplate: (template: LoadTemplate) => void;
}

export default function RepeatLoadTemplates({ onUseTemplate }: Props) {
  const { user } = useAuth();
  const [open, setOpen] = useState(true);
  const [templates, setTemplates] = useState<LoadTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetchTemplates = async () => {
      try {
        const { data, error } = await supabase
          .from("load_templates" as any)
          .select("*")
          .eq("created_by", user.id)
          .eq("is_active", true)
          .order("usage_count", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(10);

        if (error) { console.error("Template fetch error:", error); return; }
        setTemplates((data as any[]) || []);
      } catch (err) {
        console.error("RepeatLoadTemplates error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [user]);

  return (
    <PostLoadCard>
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full px-4 py-3">
        <div className="flex items-center gap-2">
          <RotateCcw className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <DemoHint hintKey="section.repeatTemplates" side="right">
            <h4 className="text-sm font-semibold text-foreground">Repeat Load Templates</h4>
          </DemoHint>
          {templates.length > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
              {templates.length}
            </span>
          )}
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      <div className="overflow-hidden transition-all duration-200" style={{ maxHeight: open ? 400 : 0, opacity: open ? 1 : 0 }}>
        <div className="px-4 pb-3 space-y-0">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs">Loading templates…</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No templates yet. Check "Save as template" when posting a load.
            </div>
          ) : (
            templates.map((t) => (
              <div key={t.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2.5 border-b border-border last:border-0">
                <div className="flex items-start sm:items-center gap-1.5 text-xs text-foreground min-w-0 flex-1 flex-wrap">
                  <span className="text-muted-foreground hidden sm:inline">▸</span>
                  <span className="font-semibold truncate max-w-full">
                    {t.origin_city}, {t.origin_state}
                  </span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-semibold truncate max-w-full">
                    {t.destination_city}, {t.destination_state}
                  </span>
                  <span className="text-muted-foreground w-full sm:w-auto truncate">
                    <span className="hidden sm:inline">– </span>{t.equipment_type || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                  {t.usage_count > 0 && (
                    <span className="text-[11px] text-muted-foreground">{t.usage_count}x used</span>
                  )}
                  <button
                    onClick={() => onUseTemplate(t)}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground"
                  >
                    Use Template
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PostLoadCard>
  );
}
